import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import {
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from 'recharts';

interface ChartData {
  name: string;
  fullName: string;
  value: number;
}

interface ChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ChartData[];
  title: string;
  chartType: 'shotType' | 'gameSituation';
}

export default function ChartModal({
  isOpen,
  onClose,
  data,
  title,
  chartType,
}: ChartModalProps) {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getGradientId = () => {
    return chartType === 'shotType' ? 'shotTypeGradient' : 'gameSituationGradient';
  };

  const renderBarLabel = (props: any) => {
    const { x, y, width, height, name, value } = props;
    
    if (!name || value === undefined || width < 30 || height < 20) {
      return <text />;
    }
    
    const labelX = x + width / 2;
    const labelY = y + height / 2;
    const fullText = `${name} (${value})`;
    const charWidth = 7;
    const lineHeight = 14;
    
    // If bar is wide enough for full text on one line, show it
    if (width >= fullText.length * charWidth + 10) {
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={12}
          fontWeight={600}
        >
          {fullText}
        </text>
      );
    }
    
    // Calculate how many characters can fit per line
    const maxCharsPerLine = Math.floor((width - 10) / charWidth);
    
    // If we can fit at least 4 characters per line and have enough height for 2 lines
    if (maxCharsPerLine >= 4 && height >= 28) {
      const words = name.split(' ');
      const valueText = `(${value})`;
      
      // Try to fit name on first line, value on second
      if (name.length <= maxCharsPerLine && valueText.length <= maxCharsPerLine) {
        return (
          <text
            x={labelX}
            y={labelY}
            fill='#ffffff'
            textAnchor='middle'
            dominantBaseline='central'
            fontSize={11}
            fontWeight={600}
          >
            <tspan x={labelX} dy={-lineHeight / 2}>
              {name}
            </tspan>
            <tspan x={labelX} dy={lineHeight}>
              {valueText}
            </tspan>
          </text>
        );
      }
      
      // If name is too long, try to wrap it intelligently
      let line1 = '';
      let line2 = '';
      
      if (words.length > 1) {
        // Try to split by words
        line1 = words[0];
        line2 = words.slice(1).join(' ') + ` (${value})`;
        
        // If second line is too long, truncate and add value
        if (line2.length > maxCharsPerLine) {
          const remainingSpace = maxCharsPerLine - ` (${value})`.length - 3;
          if (remainingSpace > 0) {
            line2 = words.slice(1).join(' ').substring(0, remainingSpace) + `... (${value})`;
          } else {
            line2 = `(${value})`;
          }
        }
      } else {
        // Single word - split the word if necessary
        if (name.length <= maxCharsPerLine) {
          line1 = name;
          line2 = `(${value})`;
        } else {
          const splitPoint = Math.max(3, maxCharsPerLine - 3);
          line1 = name.substring(0, splitPoint) + '...';
          line2 = `(${value})`;
        }
      }
      
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={10}
          fontWeight={600}
        >
          <tspan x={labelX} dy={-lineHeight / 2}>
            {line1}
          </tspan>
          <tspan x={labelX} dy={lineHeight}>
            {line2}
          </tspan>
        </text>
      );
    }
    
    // For very narrow bars, just show the value if it fits
    if (width >= value.toString().length * charWidth + 5) {
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={10}
          fontWeight={600}
        >
          {value}
        </text>
      );
    }
    
    // Bar too narrow for any meaningful text
    return <text />;
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity'
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className='relative w-full max-w-6xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-gray-200'>
          <h2 className='text-2xl font-bold text-primary-600'>
            {title}
          </h2>
          <button
            onClick={onClose}
            className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            aria-label='Close modal'
          >
            <X className='h-6 w-6 text-gray-500' />
          </button>
        </div>
        
        {/* Content */}
        <div className='p-6'>
          <div className='h-[60vh] min-h-[400px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={data}
                layout='vertical'
                margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
              >
                <defs>
                  <linearGradient
                    id='shotTypeGradient'
                    x1='0'
                    y1='0'
                    x2='1'
                    y2='0'
                  >
                    <stop offset='0%' stopColor='#3B82F6' />
                    <stop offset='50%' stopColor='#1D4ED8' />
                    <stop offset='100%' stopColor='#1E40AF' />
                  </linearGradient>
                  <linearGradient
                    id='gameSituationGradient'
                    x1='0'
                    y1='0'
                    x2='1'
                    y2='0'
                  >
                    <stop offset='0%' stopColor='#10B981' />
                    <stop offset='50%' stopColor='#059669' />
                    <stop offset='100%' stopColor='#047857' />
                  </linearGradient>
                </defs>
                <XAxis
                  type='number'
                  tickCount={Math.max(...data.map(item => item.value)) + 1}
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                  tick={{ fontSize: 14, fill: '#64748B', fontWeight: 500 }}
                  axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={20}
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                  formatter={(value: number, name: string, item: unknown) => {
                    const payload = item as {
                      payload: { fullName: string };
                    };
                    return [value, payload.payload.fullName];
                  }}
                />
                <Bar
                  dataKey='value'
                  fill={`url(#${getGradientId()})`}
                  radius={[0, 6, 6, 0]}
                  stroke='rgba(255, 255, 255, 0.5)'
                  strokeWidth={1}
                  label={renderBarLabel}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Footer with summary */}
          <div className='mt-6 p-4 bg-gray-50 rounded-lg'>
            <p className='text-sm text-gray-600'>
              <span className='font-semibold'>Total items:</span> {data.length} |{' '}
              <span className='font-semibold'>Total count:</span>{' '}
              {data.reduce((sum, item) => sum + item.value, 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}